/** Terminal statuses for POST /schedulecronjobs/job-status (llm_jobs). */
export const LLM_JOB_DONE_STATUSES = new Set([
  "completed",
  "failed",
  "cancel",
  "cancelled",
  "canceled",
]);

/** Show cancel while the LLM job is still in one of these coarse statuses. */
export const LLM_JOB_CANCELLABLE_STATUSES = new Set(["pending", "processing"]);

/**
 * Terminal statuses for POST /schedulecronjobs/video-job-status
 * (shot_video_request.current_status). Video rows have no coarse `status`.
 * `generation_failed` / `params_failed` stay running (retryable) and can be cancelled.
 */
export const VIDEO_JOB_DONE_STATUSES = new Set([
  "completed",
  "failed",
  "cancel",
  "cancelled",
  "canceled",
  "error",
  "edit_render_done",
]);

const CANCELLED_STATUSES = new Set(["cancel", "cancelled", "canceled"]);

/** Job-status spellings for a user cancel (`cancel` is what video jobs return). */
export function isCancelledStatus(value: unknown): boolean {
  return CANCELLED_STATUSES.has(normalizeJobStatus(value));
}

export function normalizeJobStatus(value: unknown): string {
  return String(value ?? "")
    .toLowerCase()
    .trim();
}

export function toPositiveJobId(value: unknown): number | null {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function getLlmJobStatus(job: any): string {
  return normalizeJobStatus(job?.status ?? job?.job_status);
}

export function getLlmJobCurrentStatus(job: any): string {
  return normalizeJobStatus(job?.current_status);
}

export function isLlmJobCancelled(job: any, status?: unknown): boolean {
  return (
    isCancelledStatus(status ?? getLlmJobStatus(job)) ||
    isCancelledStatus(getLlmJobCurrentStatus(job))
  );
}

/** Coarse or current status is a hard failure (credits exhausted, provider error). */
export function isLlmJobFailed(job: any, status?: unknown): boolean {
  if (isLlmJobCancelled(job, status)) return false;
  const coarse = normalizeJobStatus(status ?? getLlmJobStatus(job));
  const current = getLlmJobCurrentStatus(job);
  return (
    coarse === "failed" ||
    coarse === "error" ||
    current === "failed" ||
    current === "error"
  );
}

export function llmJobFailureMessage(job: any, fallback = ""): string {
  const raw =
    job?.result_data?.error ??
    job?.error_message ??
    job?.error ??
    job?.message;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return fallback;
}

export function isLlmJobDone(job: any, status?: unknown): boolean {
  const coarse = normalizeJobStatus(status ?? getLlmJobStatus(job));
  if (LLM_JOB_DONE_STATUSES.has(coarse)) return true;
  return isCancelledStatus(getLlmJobCurrentStatus(job));
}

export function isLlmJobCancellable(job: any, status?: unknown): boolean {
  if (isLlmJobDone(job, status)) return false;
  const coarse = normalizeJobStatus(status ?? getLlmJobStatus(job));
  return LLM_JOB_CANCELLABLE_STATUSES.has(coarse);
}

export function isLlmStatusDone(
  status: unknown,
  currentStatus?: unknown,
): boolean {
  return isLlmJobDone(
    currentStatus != null ? { current_status: currentStatus } : null,
    status,
  );
}

export function isLlmStatusCancellable(
  status: unknown,
  currentStatus?: unknown,
): boolean {
  return isLlmJobCancellable(
    currentStatus != null ? { current_status: currentStatus } : null,
    status,
  );
}

export function getVideoJobCurrentStatus(job: any): string {
  return normalizeJobStatus(
    job?.current_status ?? job?.status ?? job?.job_status,
  );
}

export function isVideoJobCancelled(
  job: any,
  currentStatus?: unknown,
): boolean {
  return isCancelledStatus(currentStatus ?? getVideoJobCurrentStatus(job));
}

export function isVideoJobDone(job: any, currentStatus?: unknown): boolean {
  const current = normalizeJobStatus(
    currentStatus ?? getVideoJobCurrentStatus(job),
  );
  return VIDEO_JOB_DONE_STATUSES.has(current);
}

export function isVideoJobCancellable(
  job: any,
  currentStatus?: unknown,
): boolean {
  const current = normalizeJobStatus(
    currentStatus ?? getVideoJobCurrentStatus(job),
  );
  if (!current) return false;
  return !VIDEO_JOB_DONE_STATUSES.has(current);
}

export function extractLlmJobId(source: any): number | null {
  return toPositiveJobId(
    source?.job_id ??
      source?.ai_job_id ??
      source?.llm_job_id ??
      source?.aiJob?.id ??
      source?.ai_job?.id,
  );
}

export function extractJobRecordId(job: any): number | null {
  return toPositiveJobId(job?.id ?? job?.job_id);
}

/** shot_video_request.id — never fall back to aivideos.id. */
export function extractVideoRequestId(source: any): number | null {
  return toPositiveJobId(
    source?.request_id ??
      source?.shot_video_request_id ??
      source?.shot_video_request?.id ??
      source?.video_request_id,
  );
}

export function uniquePositiveJobIds(
  values: (number | string | null | undefined)[],
): number[] {
  const ids = new Set<number>();
  for (const value of values) {
    const id = toPositiveJobId(value);
    if (id != null) ids.add(id);
  }
  return Array.from(ids);
}

export function jobCancelPayload(
  jobId: number | string | (number | string)[],
): { job_id: number | string | (number | string)[] } {
  if (Array.isArray(jobId)) {
    const ids = uniquePositiveJobIds(jobId);
    return { job_id: ids.length === 1 ? ids[0] : ids };
  }
  return { job_id: jobId };
}

/** POST /video-production/cancel — always sends `request_id` as an array. */
export function videoProductionCancelPayload(
  requestId: number | string | (number | string)[],
): { request_id: number[] } {
  const ids = Array.isArray(requestId)
    ? uniquePositiveJobIds(requestId)
    : uniquePositiveJobIds([requestId]);
  return { request_id: ids };
}
